import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities, deleteEntity, replaceEntity } from '@redux/entities'

import api from '@functions/api'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/providerDeadlineGroup/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/providerDeadlineGroup/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/providerDeadlineGroup/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/providerDeadlineGroup/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/providerDeadlineGroup/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/providerDeadlineGroup/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/providerDeadlineGroup/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/providerDeadlineGroup/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/providerDeadlineGroup/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/providerDeadlineGroup/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/providerDeadlineGroup/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/providerDeadlineGroup/DELETE_FAILURE'

// Initial State
const initialState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loading: false,
}

// Actions
export function fetchRequest(payload = {}){
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
  }
}

export function fetchSuccess(){
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors = []){
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(){
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(){
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors = []){
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(){
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(){
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors = []){
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest(){
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess(){
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors = []){
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadProviderDeadlineGroups(options){
  const { entityKey } = options || {}

  return (dispatch, getState) => {
    const loadedForKeys = [...getState().providerDeadlineGroups.loadedForKeys]
    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/provider_deadline_groups.json', options)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_DEADLINE_GROUP_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function createProviderDeadlineGroup(providerDeadlineGroup, options = {}){
  const config = {
    method: 'POST',
    data: JSON.stringify({
      provider_deadline_group: providerDeadlineGroup,
    }),
  }

  return (dispatch) => {
    dispatch(createRequest())

    const promise = api('/provider_deadline_groups.json', options, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_DEADLINE_GROUP)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function updateProviderDeadlineGroup(providerDeadlineGroup, options){
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      provider_deadline_group: providerDeadlineGroup,
    }),
  }

  return (dispatch) => {
    dispatch(updateRequest())

    const promise = api(`/provider_deadline_groups/${providerDeadlineGroup.id}.json`, options, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_DEADLINE_GROUP)
        dispatch(replaceEntity(normalizedJson))
        dispatch(updateSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function deleteProviderDeadlineGroup(providerDeadlineGroup){
  const config = {
    method: 'DELETE',
  }

  return (dispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/provider_deadline_groups/${providerDeadlineGroup.id}.json`, {}, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_DEADLINE_GROUP)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false, errors }
      })
    return promise
  }
}

export function setAsDefault(providerDeadlineGroup, options){
  const config = {
    method: 'PUT',
  }

  return (dispatch) => {
    dispatch(fetchRequest())

    const promise = api(`/provider_deadline_groups/${providerDeadlineGroup.id}/set_as_default.json`, options, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_DEADLINE_GROUP_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(state = initialState, action = {}){
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loading: true,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors,
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors,
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors,
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors,
      }
    default:
      return state
  }
}
